import React, { useState } from 'react';
import { Badge, Button, ConfirmDialog, Drawer, EmptyState, Input, Select, Spinner, Switch, useToast } from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { catalogApi, UNIT_DIMENSIONS } from './catalogApi';

const EMPTY = { name: '', code: '', dimension: 'Count', isBaseUnit: false, factorToBase: 1, isActive: true };

export default function UnitsTab({ units, loading, canManage, onChanged }) {
  const toast = useToast();
  const t = useT();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDrawerOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name ?? '',
      code: u.code ?? '',
      dimension: u.dimension ?? 'Count',
      isBaseUnit: u.isBaseUnit ?? false,
      factorToBase: u.factorToBase ?? 1,
      isActive: u.isActive ?? true,
    });
    setDrawerOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        dimension: form.dimension,
        isBaseUnit: form.isBaseUnit,
        factorToBase: form.isBaseUnit ? 1 : Number(form.factorToBase) || 1,
        isActive: form.isActive,
      };
      if (editing) await catalogApi.updateUnit(editing.id, { id: editing.id, ...payload });
      else await catalogApi.createUnit(payload);
      toast.success(editing ? t('Unit updated') : t('Unit created'));
      setDrawerOpen(false);
      await onChanged?.();
    } catch (err) {
      toast.error(t('Could not save unit'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await catalogApi.removeUnit(deleteTarget.id);
      toast.success(t('Unit deleted'));
      setDeleteTarget(null);
      await onChanged?.();
    } catch (err) {
      toast.error(t('Could not delete unit'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}><Spinner size={24} /></div>;
  }

  return (
    <>
      <div className="pp-toolbar">
        <div className="pp-toolbar__spacer" />
        {canManage && (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>
            {t('New unit')}
          </Button>
        )}
      </div>

      {units.length === 0 ? (
        <EmptyState
          icon={<Icon.Layers size={28} />}
          title={t('No units yet')}
          description={t('Define units of measure (e.g. each, kg, case) used for stock and purchasing.')}
          action={canManage ? <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('New unit')}</Button> : null}
        />
      ) : (
        <div className="cat-list">
          {units.map((u) => (
            <div className="cat-row" key={u.id}>
              <span className="cat-row__icon"><Icon.Layers size={18} /></span>
              <div className="cat-row__main">
                <span className="cat-row__name">
                  {u.name} <span className="pp-cell-muted">({u.code})</span>
                  {u.isBaseUnit && <Badge tone="brand">{t('Base')}</Badge>}
                  {!u.isActive && <Badge tone="neutral">{t('Inactive')}</Badge>}
                </span>
                <span className="cat-row__meta">
                  {t(u.dimension)}
                  {!u.isBaseUnit ? ` · ${t('×{factor} to base', { factor: u.factorToBase })}` : ''}
                </span>
              </div>
              {canManage && (
                <div className="cat-row__actions">
                  <IconButton aria-label={t('Edit unit')} variant="ghost" size="icon-sm" onClick={() => openEdit(u)}>
                    <Icon.Pencil size={15} />
                  </IconButton>
                  <IconButton aria-label={t('Delete unit')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(u)}>
                    <Icon.Trash size={15} />
                  </IconButton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => (submitting ? null : setDrawerOpen(false))}
        title={editing ? t('Edit unit') : t('New unit')}
        description={t('Units of measure (e.g. each, kg, case) used across stock and purchasing.')}
        size="md"
      >
        <form className="pp-form" onSubmit={save}>
          <div className="pp-form__row">
            <Input label={t('Name')} placeholder={t('Kilogram')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
            <Input label={t('Code')} placeholder="kg" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
          </div>
          <div className="ps-field">
            <span className="ps-field__label">{t('Dimension')}</span>
            <Select value={form.dimension} onChange={(v) => setForm((f) => ({ ...f, dimension: v }))} options={UNIT_DIMENSIONS.map((d) => ({ ...d, label: t(d.label) }))} ariaLabel={t('Dimension')} />
          </div>
          <Switch checked={form.isBaseUnit} onChange={(v) => setForm((f) => ({ ...f, isBaseUnit: v }))} label={t('Base unit for its dimension')} hint={t('The canonical unit others convert to (factor = 1).')} />
          {!form.isBaseUnit && (
            <Input label={t('Factor to base')} type="number" placeholder="1000" hint={t('How many base units equal one of this unit.')} value={form.factorToBase} onChange={(e) => setForm((f) => ({ ...f, factorToBase: e.target.value }))} />
          )}
          <Switch checked={form.isActive} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} label={t('Active')} />
          <div className="pp-form__actions">
            <Button type="button" variant="ghost" onClick={() => setDrawerOpen(false)} disabled={submitting}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={submitting}>{editing ? t('Save changes') : t('Create unit')}</Button>
          </div>
        </form>
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('Delete unit')}
        description={deleteTarget ? t('Delete "{name}"? It must not be used by any items.', { name: deleteTarget.name }) : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
        onConfirm={doDelete}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
      />
    </>
  );
}
